/**
 * Keukenbonnen per product (prototype): a category goes to its printers; a product only differs when
 * it has an exception. One tap on a category sets every product of it, exceptions included.
 */
import type { Menu, PrintingSettings, Product } from "../../api/types";
import { productsIn } from "../sale/menu-view";
import { routedPrinters } from "./output";

type Kitchen = PrintingSettings["kitchen"];

const sameSet = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((item) => b.includes(item));

/** The printers a product goes to: its exception, otherwise its category's printers. */
export function printersOf(kitchen: Kitchen, product: Product): string[] {
  return kitchen.products[product.id] ?? (product.categoryId ? (kitchen.categories[product.categoryId] ?? []) : []);
}

/** A product with its own printers (shown orange, with "zoals categorie"). */
export function isException(kitchen: Kitchen, product: Product): boolean {
  const own = kitchen.products[product.id];
  const category = product.categoryId ? (kitchen.categories[product.categoryId] ?? []) : [];
  return own !== undefined && !sameSet(own, category);
}

/** How many products of the category go to the printer (all = the category's toggle is on). */
export function routedCount(menu: Menu, kitchen: Kitchen, categoryId: string, printerId: string): number {
  return productsIn(menu, categoryId).filter((product) => printersOf(kitchen, product).includes(printerId)).length;
}

/** Drops exceptions that are equal to their category again. */
function tidy(menu: Menu, kitchen: Kitchen): Kitchen {
  const products = Object.fromEntries(
    Object.entries(kitchen.products).filter(([id, list]) => {
      const product = menu.products[id];
      if (!product?.categoryId) return false;
      return !sameSet(list, kitchen.categories[product.categoryId] ?? []);
    }),
  );
  return { categories: kitchen.categories, products };
}

const toggled = (list: readonly string[], printerId: string, on: boolean) =>
  on ? (list.includes(printerId) ? [...list] : [...list, printerId]) : list.filter((id) => id !== printerId);

/** One tap on a category: every product of it to this printer, or none (prototype). */
export function setCategory(menu: Menu, kitchen: Kitchen, categoryId: string, printerId: string, on: boolean): Kitchen {
  const categories = {
    ...kitchen.categories,
    [categoryId]: toggled(kitchen.categories[categoryId] ?? [], printerId, on),
  };
  const products = { ...kitchen.products };
  for (const product of productsIn(menu, categoryId)) {
    const own = products[product.id];
    if (own !== undefined) products[product.id] = toggled(own, printerId, on);
  }
  return tidy(menu, { categories, products });
}

/** One product to this printer or not: an exception unless it ends up like its category. */
export function setProduct(menu: Menu, kitchen: Kitchen, product: Product, printerId: string, on: boolean): Kitchen {
  const products = { ...kitchen.products, [product.id]: toggled(printersOf(kitchen, product), printerId, on) };
  return tidy(menu, { categories: kitchen.categories, products });
}

/** "zoals categorie": the product follows its category again. */
export function resetProduct(kitchen: Kitchen, product: Product): Kitchen {
  const products = Object.fromEntries(Object.entries(kitchen.products).filter(([id]) => id !== product.id));
  return { categories: kitchen.categories, products };
}

/**
 * Nothing set up for the kitchen of a table: no printer on "Keukenbon" for tables with a category or
 * product on it, and no printer on "Keukenbon (alles)". A table to the kitchen then prints nothing there
 * (Papermoon, 08/10/2026), as the server says too ("unrouted").
 */
export function nothingRouted(settings: PrintingSettings, printers: readonly { id: string }[]): boolean {
  const routed = routedPrinters(settings.kitchen);
  return !printers.some((printer) => {
    const kitchen = settings.output[printer.id]?.table.kitchen;
    return kitchen === "all" || (kitchen === "ticket" && routed.has(printer.id));
  });
}
